'use client';

import { Anchor } from '@zillow/constellation-10';
import { ExternalLink } from 'lucide-react';
import dynamic from 'next/dynamic';
import type { ReactNode } from 'react';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { analytics } from '@/src/components/features/property/lib';
import { ReadMoreText } from '@/src/components/features/property/lib/ReadMoreText';
import { PropertyManagerFooter } from '@/src/components/features/property/PropertyManagerModule/PropertyManagerFooter';
import { LucideIcon } from '@/src/components/LucideIcon';

type ListingProps = {
  listing: PropertyDetailListing;
};

type HdpReadMoreButtonProps = {
  content: string;
  controlsId: string;
  maxTruncatedLength?: number;
  readMoreText: string;
  readLessText: string;
  'aria-label'?: string;
};

const ExpiredListingBanner = dynamic<ListingProps>(() =>
  import('@/src/components/features/property/ExpiredListingBanner').then((mod) => mod.ExpiredListingBanner),
);

type TrackedBuildingWebsiteLinkProps = {
  websiteUrl: string;
  linkText: string;
  ariaLabel?: string;
  maloneLotIdEncoded?: string;
};

export function HdpReadMoreButton(props: HdpReadMoreButtonProps) {
  return <ReadMoreText {...props} />;
}

export function LazyPropertyManagerFooter(props: ListingProps) {
  return <PropertyManagerFooter {...props} />;
}

export function LazyExpiredListingBanner(props: ListingProps) {
  return <ExpiredListingBanner {...props} />;
}

type TrackedContactPhoneLinkProps = {
  listing: PropertyDetailListing;
  href: string;
  className?: string;
  children: ReactNode;
};

export function TrackedContactPhoneLink({ listing, href, className, children }: TrackedContactPhoneLinkProps) {
  return (
    <a href={href} className={className} onClick={() => analytics.emitTrackPhoneLeadEvent({ listing })}>
      {children}
    </a>
  );
}

export function TrackedBuildingWebsiteLink({
  websiteUrl,
  linkText,
  ariaLabel,
  maloneLotIdEncoded,
}: TrackedBuildingWebsiteLinkProps) {
  const handleWebsiteClick = () => {
    analytics.sendEventToApi('building_website_visited');
    if (maloneLotIdEncoded) {
      analytics.sendEventToApi('building_website_clicked', maloneLotIdEncoded);
    }
    analytics.emitTrackLowerPpcClickEvent({ triggerLocation: 'home_details_halo' });
    analytics.emitTrackBuildingPayPerLinkClickEvent({
      triggerObject: 'home_details_component|property_details',
      triggerLocation: 'home_details_halo',
    });
  };

  return (
    <Anchor asChild>
      <a
        href={websiteUrl}
        target="_blank"
        rel="nofollow noopener noreferrer"
        onClick={handleWebsiteClick}
        aria-label={ariaLabel}
      >
        {linkText}
        <LucideIcon icon={ExternalLink} size="sm" />
      </a>
    </Anchor>
  );
}
